import React, { useState, useRef } from "react";
import * as mammoth from "mammoth";
import { getCorrections } from "../taskpane/utils/getCorrections";
import { cleanText } from "../taskpane/utils/cleanText";

const ADDIN_API_URL = process.env.ADDIN_API_URL;

export const App: React.FC = () => {
  const [service, setService] = useState("");
  const [jobId, setJobId] = useState("");
  const [category, setCategory] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generalImprovement, setGeneralImprovement] = useState(false);
  const [applyHouseStyle, setApplyHouseStyle] = useState(false);
  const [printFilters, setPrintFilters] = useState(false);
  const [printRequest, setPrintRequest] = useState(false);
  const [output, setOutput] = useState<JSX.Element | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  abortControllerRef.current = new AbortController();

  const docxBufferRef = useRef<ArrayBuffer | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      docxBufferRef.current = reader.result as ArrayBuffer;
      setOutput(<p>File loaded. Click "Read Paragraphs".</p>);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRead = async () => {
    setIsLoading(true);
    const buffer = docxBufferRef.current;
    if (!buffer) {
      setOutput(<p style={{ color: "red" }}>No file loaded.</p>);
      return;
    }

    try {
      const result = await mammoth.extractRawText({ arrayBuffer: buffer });
      const paragraphs = result.value
        .split(/\n+/)
        .filter((p) => p.trim().length > 0)
        .map((t) => cleanText(t));

      const correctionResult = await getCorrections({
        apiUrl: apiUrl || ADDIN_API_URL,
        category,
        paragraphs: paragraphs.map((p, index) => ({ text: p, index })) as any,
        service,
        printFilters,
        abortControllerRef,
        skipRequest: false,
        applyHouseStyle,
        generalImprovement,
        setJobId
      });

      const corrections = correctionResult.corrections || [];
      const hasCorrections = corrections.length > 0;

      setOutput(
        <div style={{ marginTop: "2rem" }}>
          {hasCorrections ? (
            <>
              <h3>Old text:</h3>
              <ul>
                {corrections.map((c, i) => (
                  <>
                    <li key={i}>{c.text}</li>
                  </>
                ))}
              </ul>

              <h3>New text:</h3>
              <ul>
                {corrections.map((c, i) => (
                  <>
                    <li key={i}>{c.corrected}</li>
                  </>
                ))}
              </ul>

              <h3>Corrections:</h3>
              <ul>
                {corrections.map((c, i) => (
                  <>
                    <li key={i}>{c.text}</li>
                    <li key={i}>{c.corrected}</li>
                    <br></br>
                  </>
                ))}
              </ul>
            </>
          ) : (
            <p>
              <strong>No corrections returned.</strong>
            </p>
          )}

          {correctionResult.error && (
            <p style={{ color: "red" }}>
              <strong>Error:</strong> {correctionResult.error.message}
            </p>
          )}

          {printRequest && (
            <>
              <details>
                <summary>Request Info</summary>
                <pre>{JSON.stringify(correctionResult.requestInfo, null, 2)}</pre>
              </details>
              <details>
                <summary>Response Info</summary>
                <pre>{JSON.stringify(correctionResult.responseInfo, null, 2)}</pre>
              </details>
            </>
          )}
        </div>
      );
    } catch (err: any) {
      console.error(err);
      setOutput(
        <div style={{ color: "red" }}>
          <p>Error reading or processing docx file.</p>
          <pre>{err.message}</pre>
        </div>
      );
    }
    setIsLoading(false);
  };

  return (
    <div style={{ fontFamily: "sans-serif", padding: "2rem" }}>
      <div>{jobId}</div>
      
      <div style={{ marginBottom: "1rem" }}>
        <label>
          Service:
          <input
            disabled={isLoading}
            type="text"
            value={service}
            onChange={(e) => setService(e.target.value)}
            style={{ marginLeft: "0.5rem", width: "300px" }}
          />
        </label>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label>
          Category:
          <input
            disabled={isLoading}
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{ marginLeft: "0.5rem", width: "300px" }}
          />
        </label>
      </div>

       <div style={{ marginBottom: "1rem" }}>
        <label>
          <input
            disabled={isLoading}
            type="checkbox"
            checked={generalImprovement}
            onChange={(e) => setGeneralImprovement(e.target.checked)}
          />{" "}
          General writing improvement
        </label>
      </div>

       <div style={{ marginBottom: "1rem" }}>
        <label>
          <input
            disabled={isLoading}
            type="checkbox"
            checked={applyHouseStyle}
            onChange={(e) => setApplyHouseStyle(e.target.checked)}
          />{" "}
          House style rules
        </label>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label>
          Custom API URL:
          <input
            disabled={isLoading}
            type="text"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            style={{ marginLeft: "0.5rem", width: "300px" }}
          />
        </label>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label>
          <input
            disabled={isLoading}
            type="checkbox"
            checked={printFilters}
            onChange={(e) => setPrintFilters(e.target.checked)}
          />{" "}
          Use Simplified endpoint
        </label>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label>
          <input
            disabled={isLoading}
            type="checkbox"
            checked={printRequest}
            onChange={(e) => setPrintRequest(e.target.checked)}
          />{" "}
          Print request info at the end of the doc
        </label>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <input disabled={isLoading} type="file" accept=".docx" onChange={handleFileUpload} />
      </div>

      <button disabled={isLoading} onClick={handleRead}>
        Read Paragraphs
      </button>

      {output}
    </div>
  );
};
